import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banknote, Calculator, RefreshCcw, ShieldAlert, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { api, type PaginatedData } from '@/lib/api';
import {
  formatMoney,
  type DepotBillableCharge,
  type DepotContainerVisit,
  type DepotRepairJob,
  type DepotWashJob,
  useDepotList,
} from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type DepotRateCard = {
  id: string;
  data?: {
    module?: string;
    billToName?: string;
    chargeType?: string;
  } | null;
};

type ProfitabilityRow = {
  accountName: string;
  revenueSignal: number;
  directCost: number;
  contribution: number;
  marginPct: number;
  tariffCovered: boolean;
  chargeCount: number;
  uncoveredChargeCount: number;
  uncoveredRevenue: number;
  repairJobs: number;
  washJobs: number;
};

function normalizeKey(value?: string | null): string {
  return value?.trim() || 'Unassigned';
}

function toAmount(value: string | number | null | undefined): number {
  const parsed = typeof value === 'string' ? Number(value) : value ?? 0;
  return Number.isFinite(parsed) ? Number(parsed) : 0;
}

export default function DepotProfitabilityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const charges = useDepotList<DepotBillableCharge>('/depot/charges/billable?limit=100');
  const containers = useDepotList<DepotContainerVisit>('/depot/containers?limit=100');
  const repairJobs = useDepotList<DepotRepairJob>('/depot/repair-jobs?limit=100');
  const washJobs = useDepotList<DepotWashJob>('/depot/wash-jobs?limit=100');
  const [rateCards, setRateCards] = useState<DepotRateCard[]>([]);
  const [rateCardError, setRateCardError] = useState<string | null>(null);
  const [rateCardLoading, setRateCardLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const accountFilter = searchParams.get('account')?.trim() ?? '';

  const error = charges.error ?? containers.error ?? repairJobs.error ?? washJobs.error ?? rateCardError;
  const loading = charges.loading || containers.loading || repairJobs.loading || washJobs.loading || rateCardLoading;

  const pageError: DataErrorDetail | null = error
    ? {
        endpoint: '/depot/charges/billable + /depot/containers + /depot/repair-jobs + /depot/wash-jobs + /rate-cards',
        message: error,
        status: 500,
        code: 'DEPOT_PROFITABILITY_LOAD_FAILED',
      }
    : null;

  const loadRateCards = async () => {
    setRateCardLoading(true);
    setRateCardError(null);
    try {
      const response = await api.get<PaginatedData<DepotRateCard>>('/rate-cards?limit=100');
      setRateCards(response.items.filter((item) => item.data?.module === 'depot'));
    } catch (err) {
      setRateCardError(err instanceof Error ? err.message : 'Failed to load depot tariffs');
    } finally {
      setRateCardLoading(false);
    }
  };

  useEffect(() => {
    void loadRateCards();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([charges.refresh(), containers.refresh(), repairJobs.refresh(), washJobs.refresh(), loadRateCards()]);
    setRefreshing(false);
  };

  const rows = useMemo(() => {
    const coveredAccounts = new Set(rateCards.map((item) => normalizeKey(item.data?.billToName)));
    const coveredChargeKeys = new Set(
      rateCards.map((item) => `${normalizeKey(item.data?.billToName)}::${normalizeKey(item.data?.chargeType)}`),
    );
    const visitsById = new Map(containers.items.map((item) => [item.id, item]));
    const map = new Map<string, ProfitabilityRow>();
    const ensure = (name?: string | null) => {
      const key = normalizeKey(name);
      const current = map.get(key);
      if (current) return current;
      const next: ProfitabilityRow = {
        accountName: key,
        revenueSignal: 0,
        directCost: 0,
        contribution: 0,
        marginPct: 0,
        tariffCovered: coveredAccounts.has(key),
        chargeCount: 0,
        uncoveredChargeCount: 0,
        uncoveredRevenue: 0,
        repairJobs: 0,
        washJobs: 0,
      };
      map.set(key, next);
      return next;
    };

    charges.items.forEach((item) => {
      const row = ensure(item.bill_to_name ?? item.line_operator);
      const chargeKey = `${row.accountName}::${normalizeKey(item.charge_type)}`;
      const isCovered = coveredChargeKeys.has(chargeKey);
      row.revenueSignal += toAmount(item.amount);
      row.chargeCount += 1;
      row.tariffCovered = row.tariffCovered || isCovered;
      if (!isCovered) {
        row.uncoveredChargeCount += 1;
        row.uncoveredRevenue += toAmount(item.amount);
      }
    });

    repairJobs.items.forEach((item) => {
      const visit = item.visit_id ? visitsById.get(item.visit_id) : null;
      const row = ensure(visit?.line_operator ?? item.depot_code);
      row.directCost += (toAmount(item.labor_hours) * 35) + toAmount(item.material_cost);
      row.repairJobs += 1;
    });

    washJobs.items.forEach((item) => {
      const visit = item.visit_id ? visitsById.get(item.visit_id) : null;
      const row = ensure(visit?.line_operator ?? item.depot_code);
      row.directCost += 25;
      row.washJobs += 1;
    });

    const rows = Array.from(map.values())
      .map((item) => {
        const contribution = item.revenueSignal - item.directCost;
        const marginPct = item.revenueSignal > 0 ? (contribution / item.revenueSignal) * 100 : 0;
        const tariffCovered = item.chargeCount > 0 ? item.uncoveredChargeCount === 0 : item.tariffCovered;
        return { ...item, contribution, marginPct, tariffCovered };
      })
      .sort((a, b) => b.revenueSignal - a.revenueSignal);
    if (!accountFilter) return rows;
    const filterKey = normalizeKey(accountFilter);
    return rows.filter((item) => normalizeKey(item.accountName) === filterKey);
  }, [accountFilter, charges.items, containers.items, rateCards, repairJobs.items, washJobs.items]);

  const totals = useMemo(() => {
    const revenueSignal = rows.reduce((sum, item) => sum + item.revenueSignal, 0);
    const directCost = rows.reduce((sum, item) => sum + item.directCost, 0);
    const contribution = revenueSignal - directCost;
    const unattributedCost = rows
      .filter((item) => item.accountName === 'Unassigned')
      .reduce((sum, item) => sum + item.directCost, 0);
    const uncoveredRevenue = rows.reduce((sum, item) => sum + item.uncoveredRevenue, 0);
    return {
      revenueSignal,
      directCost,
      contribution,
      coveredAccounts: rows.filter((item) => item.tariffCovered).length,
      unattributedCost,
      uncoveredRevenue,
    };
  }, [rows]);

  const uncoveredCharges = useMemo(() => {
    const coveredChargeKeys = new Set(
      rateCards.map((item) => `${normalizeKey(item.data?.billToName)}::${normalizeKey(item.data?.chargeType)}`),
    );

    return charges.items
      .map((item) => {
        const accountName = normalizeKey(item.bill_to_name ?? item.line_operator);
        const chargeType = normalizeKey(item.charge_type);
        return {
          id: item.id,
          chargeNo: item.charge_no,
          accountName,
          chargeType,
          amount: toAmount(item.amount),
          covered: coveredChargeKeys.has(`${accountName}::${chargeType}`),
        };
      })
      .filter((item) => !item.covered)
      .sort((a, b) => b.amount - a.amount);
  }, [charges.items, rateCards]);

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Profitability" onRetry={handleRefresh} />}

      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Depot Profitability</h2>
          <p className="text-sm text-muted-foreground">
            Contribution view across billable charges, repair labor/material cost, wash execution, and tariff coverage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/revenue">Revenue</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/tariff">Tariff</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
            <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {accountFilter && (
            <Button variant="ghost" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
              Clear Filter
            </Button>
          )}
        </div>
      </div>

      {accountFilter && (
        <div className="text-xs text-muted-foreground">
          Filtered account: <span className="font-medium text-foreground">{accountFilter}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {[
          { label: 'Revenue Signal', value: formatMoney(totals.revenueSignal), icon: <Banknote className="h-4 w-4 text-sky-700" /> },
          { label: 'Direct Cost', value: formatMoney(totals.directCost), icon: <Wrench className="h-4 w-4 text-rose-700" /> },
          { label: 'Contribution', value: formatMoney(totals.contribution), icon: <Calculator className="h-4 w-4 text-emerald-700" /> },
          { label: 'Tariff Covered Accounts', value: totals.coveredAccounts, icon: <ShieldAlert className="h-4 w-4 text-violet-700" /> },
          { label: 'Unattributed Cost', value: formatMoney(totals.unattributedCost), icon: <ShieldAlert className="h-4 w-4 text-amber-700" /> },
          { label: 'Tariff Gap Revenue', value: formatMoney(totals.uncoveredRevenue), icon: <ShieldAlert className="h-4 w-4 text-orange-700" /> },
        ].map((item) => (
          <div key={item.label} className="rounded-lg border bg-background px-3 py-3">
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{item.label}</span>
              {item.icon}
            </div>
            <div className="text-2xl font-bold">{item.value}</div>
          </div>
        ))}
      </div>

      <DepotGridPanel
        title="Account Contribution"
        subtitle="Revenue, direct cost, contribution, margin and tariff coverage by account."
        action={<Button variant="ghost" size="sm" asChild><Link to="/depot/maintenance">Maintenance Cost</Link></Button>}
      >
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Account</th>
              <th className={depotThClassName}>Revenue</th>
              <th className={depotThClassName}>Cost</th>
              <th className={depotThClassName}>Contribution</th>
              <th className={depotThClassName}>Margin</th>
              <th className={depotThClassName}>Repair / Wash</th>
              <th className={depotThClassName}>Charges</th>
              <th className={depotThClassName}>Gap</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No profitability rows yet.</DepotEmptyRow>
            ) : (
              rows.map((item) => (
                <tr key={item.accountName} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-medium`}>
                    {item.accountName}
                    <div className="mt-1"><Badge variant={item.tariffCovered ? 'secondary' : 'outline'}>{item.tariffCovered ? 'tariff covered' : 'coverage gap'}</Badge></div>
                  </td>
                  <td className={depotTdClassName}>{formatMoney(item.revenueSignal)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.directCost)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.contribution)}</td>
                  <td className={depotTdClassName}>{item.marginPct.toFixed(1)}%</td>
                  <td className={depotTdClassName}>{item.repairJobs}/{item.washJobs}</td>
                  <td className={depotTdClassName}>{item.chargeCount}</td>
                  <td className={depotTdClassName}>{formatMoney(item.uncoveredRevenue)} · {item.uncoveredChargeCount} lines</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel
        title="Tariff Gap Drivers"
        subtitle="Billable charge rows that do not have tariff template coverage."
        action={<Button variant="ghost" size="sm" asChild><Link to="/depot/tariff">Open Tariff</Link></Button>}
      >
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Charge</th>
              <th className={depotThClassName}>Account</th>
              <th className={depotThClassName}>Charge Type</th>
              <th className={depotThClassName}>Gap Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {uncoveredCharges.length === 0 ? (
              <DepotEmptyRow colSpan={4}>All current billable charges are covered by tariff templates.</DepotEmptyRow>
            ) : (
              uncoveredCharges.slice(0, 10).map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.chargeNo}</td>
                  <td className={depotTdClassName}>{item.accountName}</td>
                  <td className={depotTdClassName}><Badge variant="outline">{item.chargeType}</Badge></td>
                  <td className={depotTdClassName}>{formatMoney(item.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>
    </div>
  );
}
